interface ApiErrorData {
  error?: string;
  message?: string;
  status?: string;
  rejection?: Rejection | null;
}

interface ApiError {
  data?: ApiErrorData;
}

interface Rejection {
  code: string;
  message: string;
}

const ROLE_CONFLICT_MESSAGE =
  'The acting admin cannot be selected as the coach or member. No scheduling changes were made.';

export interface AdminAppointmentErrorGuidance {
  title: string;
  detail: string;
}

const CALENDAR_CONFLICT_GUIDANCE: Record<string, AdminAppointmentErrorGuidance> = {
  calendar_conflict_check_unavailable: {
    title: 'Calendar conflicts could not be checked',
    detail:
      'Calendar conflict checks are unavailable, so this failure cannot be overridden. Retry when calendar checking is available. No scheduling changes were made.',
  },
  calendar_conflict_override_required: {
    title: 'Calendar conflict confirmation required',
    detail:
      'The conflict changed or confirmation was not provided. Review the selected time and confirm again. No scheduling changes were made.',
  },
};

export const COACHING_ADOPT_MESSAGE =
  'Series linkage unavailable; use native coaching booking or escalate for reconciliation; no calendar changes made.';

export const ENGAGEMENT_SCHEDULING_GUIDANCE = {
  member_engagement_end_missing: {
    title: 'Set up your engagement to schedule',
    detail:
      'Add an engagement end date for this member, then try again. No scheduling changes were made.',
  },
  member_engagement_ended: {
    title: 'Your engagement has ended',
    detail:
      "Extend or renew this member's engagement before scheduling. No scheduling changes were made.",
  },
  member_engagement_unavailable: {
    title: 'Scheduling is temporarily unavailable',
    detail:
      "We couldn't verify this member's engagement. Try again later or contact support. No scheduling changes were made.",
  },
} as const;

export function adminAppointmentErrorMessage(
  error?: unknown,
  rejection?: Rejection | null
): string {
  const conflictGuidance = adminAppointmentErrorGuidance(error, rejection);
  if (conflictGuidance) return `${conflictGuidance.title}. ${conflictGuidance.detail}`;

  const rejectionMessage = engagementSchedulingMessage(rejection?.code);
  if (rejectionMessage) return rejectionMessage;
  if (rejection?.message) return rejection.message;

  const structuredRejection = apiErrorData(error)?.rejection;
  const structuredRejectionMessage = engagementSchedulingMessage(structuredRejection?.code);
  if (structuredRejectionMessage) return structuredRejectionMessage;
  if (structuredRejection?.message) return structuredRejection.message;

  const code = apiErrorCode(error);
  const engagementMessage = engagementSchedulingMessage(code);
  if (engagementMessage) return engagementMessage;
  if (code === 'admin_actor_role_conflict' || code === 'admin_role_conflict') {
    return ROLE_CONFLICT_MESSAGE;
  }
  if (code === 'coaching_adopt_unsupported') return COACHING_ADOPT_MESSAGE;

  const message = apiErrorData(error)?.message;
  return message || 'No scheduling changes were made. Please review the details and try again.';
}

export function adminAppointmentErrorGuidance(
  error?: unknown,
  rejection?: Rejection | null
): AdminAppointmentErrorGuidance | undefined {
  const data = apiErrorData(error);
  const code = rejection?.code ?? data?.rejection?.code ?? data?.error;
  return code ? CALENDAR_CONFLICT_GUIDANCE[code] : undefined;
}

function engagementSchedulingMessage(code: string | undefined): string | undefined {
  const guidance = engagementSchedulingGuidance(code);
  return guidance ? `${guidance.title}. ${guidance.detail}` : undefined;
}

export function engagementSchedulingGuidance(code: string | undefined) {
  if (!code || !(code in ENGAGEMENT_SCHEDULING_GUIDANCE)) return undefined;
  return ENGAGEMENT_SCHEDULING_GUIDANCE[code as keyof typeof ENGAGEMENT_SCHEDULING_GUIDANCE];
}

export function isAdminAppointmentTerminalRejection(error: unknown): boolean {
  const data = apiErrorData(error);
  return data?.status === 'rejected' && Boolean(data.rejection?.message);
}

function apiErrorCode(error: unknown): string | undefined {
  return apiErrorData(error)?.error;
}

function apiErrorData(error: unknown): ApiErrorData | undefined {
  if (typeof error !== 'object' || error === null || !('data' in error)) return undefined;
  const data = (error as ApiError).data;
  return typeof data === 'object' && data !== null ? data : undefined;
}
